import { useCallback, useEffect, useState, createContext, useContext } from "react";

import { branding as brandingApi, type Branding } from "@/api/branding";

interface BrandingValue {
  data: Branding | null;
  refresh: () => Promise<void>;
}

const BrandingCtx = createContext<BrandingValue>({
  data: null,
  refresh: async () => undefined,
});

export function useBranding(): Branding | null {
  return useContext(BrandingCtx).data;
}

/**
 * Resolves branding at boot from /api/v1/branding and applies the primary
 * color + title + favicon. On the shop host (`shop.piggytrader.cc`) the Host
 * won't match a tenant slug, so the backend returns the DEFAULT (Piggy)
 * branding — which is exactly what we want for the central storefront. A
 * `?org=` query still lets a broker co-brand the chrome later, for free.
 */
export function BrandingLoader({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<Branding | null>(null);

  const load = useCallback(async () => {
    try {
      const slug = new URL(window.location.href).searchParams.get("org") ?? undefined;
      const b = await brandingApi.load(slug ?? "default");
      setData(b);
      applyBranding(b);
    } catch {
      // Best-effort; default styling already applied via CSS.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return <BrandingCtx.Provider value={{ data, refresh: load }}>{children}</BrandingCtx.Provider>;
}

function hexToRgbTriplet(hex: string): string | null {
  const h = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function applyBranding(b: Branding) {
  // The central Piggy shop keeps its designed emerald + pig favicon. Only a
  // genuinely co-branded entry (?org=acme / a custom domain) re-themes the
  // accent + title, so a broker can white-label the storefront for free.
  if (b.matched_via === "default") return;

  const primary = b.branding.primary_color;
  if (primary) {
    const rgb = hexToRgbTriplet(primary);
    if (rgb) document.documentElement.style.setProperty("--brand-color", rgb);
  }
  if (b.name) document.title = `${b.name} • Shop`;
  if (b.branding.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b.branding.favicon_url;
  }
}
